import { type ReactNode, useEffect, useRef, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  LoaderCircle,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnhanceSuggestionPanel } from "@/components/ai/enhance-suggestion-panel";
import { PolishDiffPanel } from "@/components/ai/polish-diff-panel";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { StoryEditorSaveState } from "@/components/story/story-workspace-header";
import {
  LoreEditor,
  type RichTextEditorApi,
} from "@/editors/rich-text/lore-editor";
import type { CapturedSelectionEnhanceRange } from "@/editors/rich-text/selectionEnhance";
import { chapterStatuses, type ChapterView } from "@/features/story";
import type {
  EnhanceState,
  PolishState,
} from "@/stores/storyWorkspaceStore";

type ChapterEditorProps = {
  afterEditor?: ReactNode;
  headerActions?: ReactNode;
  chapter: ChapterView;
  focusText?: string | null;
  isOrderClosing?: boolean;
  isOrderOpening?: boolean;
  onGenerateHeader?: () => Promise<void>;
  onSave: (input: {
    body: JSONContent;
    status: ChapterView["status"];
    title: string;
  }) => Promise<void>;
  onSaveStateChange?: (
    state: StoryEditorSaveState,
    error: string | null,
  ) => void;
  onRemoveHeaderImage?: () => Promise<void>;
  onUploadHeaderImage?: (file: File) => Promise<void>;
  textEnhancement?: {
    getEnhanceCreditCost: (wordCount: number) => number;
    getEnhanceDisabledReason: (wordCount: number) => string | null;
    enhanceState: EnhanceState;
    onDismissEnhance: () => void;
    onDismissPolish: () => void;
    onPromptAction: (prompt: EnhanceState["prompt"] | PolishState["prompt"]) => void;
    onRequestSelectionEnhance: (selection: CapturedSelectionEnhanceRange) => void;
    onSelectionChanged: () => void;
    polishState: PolishState;
  };
};

const SAVE_DELAY_MS = 800;
const CHAPTER_HEADER_STORAGE_KEY_PREFIX = "story:chapter-header-collapsed:";
const EMPTY_PARAGRAPH: JSONContent = {
  type: "paragraph",
};

function collectNodeText(node: JSONContent | null | undefined): string {
  if (!node) {
    return "";
  }

  if (typeof node.text === "string") {
    return node.text;
  }

  if (!Array.isArray(node.content)) {
    return "";
  }

  return node.content.map((child) => collectNodeText(child)).join("");
}

function normalizeTitleText(value: string | null | undefined) {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function buildTitleNode(title: string): JSONContent {
  return {
    attrs: {
      level: 1,
    },
    content: title
      ? [
          {
            text: title,
            type: "text",
          },
        ]
      : undefined,
    type: "heading",
  };
}

function ensureTitleInBody(body: JSONContent, title: string): JSONContent {
  const normalizedTitle = normalizeTitleText(title);
  const content = Array.isArray(body.content) ? [...body.content] : [];
  const firstNode = content[0];
  const firstNodeTitle = normalizeTitleText(collectNodeText(firstNode));

  if (!firstNode || firstNodeTitle.length === 0) {
    return {
      ...body,
      content: [buildTitleNode(normalizedTitle), EMPTY_PARAGRAPH, ...content],
    };
  }

  if (firstNodeTitle === normalizedTitle) {
    return body;
  }

  return {
    ...body,
    content: [buildTitleNode(normalizedTitle), ...content],
  };
}

function extractTitleFromBody(body: JSONContent, fallbackTitle: string) {
  const content = Array.isArray(body.content) ? body.content : [];

  for (const node of content) {
    const candidate = normalizeTitleText(collectNodeText(node));

    if (candidate.length > 0) {
      return candidate;
    }
  }

  return normalizeTitleText(fallbackTitle);
}

function readCollapsedState(chapterId: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem(
      `${CHAPTER_HEADER_STORAGE_KEY_PREFIX}${chapterId}`,
    ) === "true"
  );
}

export function ChapterEditor({
  afterEditor,
  headerActions,
  chapter,
  focusText = null,
  isOrderClosing = false,
  isOrderOpening = false,
  onGenerateHeader,
  onSave,
  onSaveStateChange,
  onRemoveHeaderImage,
  onUploadHeaderImage,
  textEnhancement,
}: ChapterEditorProps) {
  const { t } = useTranslation();
  const [body, setBody] = useState(() => ensureTitleInBody(chapter.body, chapter.title));
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsedState(chapter.id),
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<StoryEditorSaveState>("idle");
  const [status, setStatus] = useState(chapter.status);
  const chapterResetRef = useRef(chapter);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const editorApiRef = useRef<RichTextEditorApi | null>(null);
  const onSaveRef = useRef(onSave);
  const documentTitle = extractTitleFromBody(body, chapter.title);
  const toggleLabel = collapsed
    ? t("story.chapters.expandHeader", {
        defaultValue: "Expand chapter header",
      })
    : t("story.chapters.collapseHeader", {
        defaultValue: "Collapse chapter header",
      });
  const statusContent =
    saveState === "saving" ? (
      <>
        <LoaderCircle className="h-4 w-4 animate-spin" />
        {t("story.save.saving")}
      </>
    ) : saveState === "error" ? (
      <>
        <TriangleAlert className="h-4 w-4" />
        {t("story.save.error")}
      </>
    ) : null;

  useEffect(() => {
    chapterResetRef.current = chapter;
  }, [chapter]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    const currentChapter = chapterResetRef.current;

    setBody(ensureTitleInBody(currentChapter.body, currentChapter.title));
    setCollapsed(readCollapsedState(currentChapter.id));
    setSaveError(null);
    setSaveState("idle");
    setStatus(currentChapter.status);
  }, [chapter.id]);

  useEffect(() => {
    onSaveStateChange?.(saveState, saveError);
  }, [onSaveStateChange, saveError, saveState]);

  useEffect(() => {
    if (!focusText) {
      return;
    }

    const timeout = window.setTimeout(() => {
      editorApiRef.current?.selectBestMatchingText(focusText);
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [chapter.id, focusText]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      `${CHAPTER_HEADER_STORAGE_KEY_PREFIX}${chapter.id}`,
      String(collapsed),
    );
  }, [chapter.id, collapsed]);

  useEffect(() => {
    const nextTitle = extractTitleFromBody(body, chapter.title);
    const nextState = {
      body,
      status,
      title: nextTitle,
    };

    if (
      JSON.stringify(nextState.body) ===
        JSON.stringify(ensureTitleInBody(chapter.body, chapter.title)) &&
      nextState.status === chapter.status &&
      nextState.title === chapter.title
    ) {
      return;
    }

    setSaveState("saving");
    const timeout = window.setTimeout(() => {
      void (async () => {
        try {
          await onSaveRef.current(nextState);
          setSaveError(null);
          setSaveState("saved");
        } catch (error) {
          setSaveError(
            error instanceof Error
              ? error.message
              : t("story.messages.saveError"),
          );
          setSaveState("error");
        }
      })();
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [body, chapter.body, chapter.status, chapter.title, status, t]);

  function getRoleHint() {
    if (isOrderOpening && isOrderClosing) {
      return t("story.chapters.autoRoleSingle", {
        defaultValue:
          "This chapter is both Opening and Closing because it is the only chapter in the rail.",
      });
    }

    if (isOrderOpening) {
      return t("story.chapters.autoRoleOpening", {
        defaultValue:
          "This chapter is marked as Opening automatically because it is first in the chapter rail.",
      });
    }

    if (isOrderClosing) {
      return t("story.chapters.autoRoleClosing", {
        defaultValue:
          "This chapter is marked as Closing automatically because it is last in the chapter rail.",
      });
    }

    return t("story.chapters.autoRoleHint", {
      defaultValue:
        "Opening and Closing are now assigned automatically from the chapter order in the rail.",
    });
  }

  async function handleUploadHeaderImage(file: File | null) {
    if (!file || !onUploadHeaderImage) {
      return;
    }

    setSaveState("saving");

    try {
      await onUploadHeaderImage(file);
      setSaveError(null);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : t("story.messages.saveError"),
      );
      setSaveState("error");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function handleRemoveHeaderImage() {
    if (!onRemoveHeaderImage) {
      return;
    }

    setSaveState("saving");

    try {
      await onRemoveHeaderImage();
      setSaveError(null);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : t("story.messages.saveError"),
      );
      setSaveState("error");
    }
  }

  function handleReplaceEnhanceSuggestion() {
    const state = textEnhancement?.enhanceState;
    if (
      !state?.suggestion
      || typeof state.selectionFrom !== "number"
      || typeof state.selectionTo !== "number"
    ) {
      return;
    }

    editorApiRef.current?.replaceRangeText(
      state.selectionFrom,
      state.selectionTo,
      state.suggestion,
    );
    textEnhancement?.onDismissEnhance();
  }

  function handleApplyPolishSuggestion() {
    const suggestion = textEnhancement?.polishState.suggestion;
    if (!suggestion) {
      return;
    }

    editorApiRef.current?.replaceDocumentText(suggestion);
    textEnhancement?.onDismissPolish();
  }

  return (
    <section className={collapsed ? "space-y-3" : "space-y-6"}>
      <header
        className={
          collapsed
            ? "space-y-3 px-1 transition-all duration-300 ease-out"
            : "space-y-4 px-1 transition-all duration-300 ease-out"
        }
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={
                  collapsed
                    ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                    : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"
                }
              >
                {t("story.modes.chapters")}
              </Badge>
              {isOrderOpening ? (
                <Badge
                  variant="outline"
                  className={
                    collapsed
                      ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                      : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"
                  }
                >
                  {t("story.chapters.opening")}
                </Badge>
              ) : null}
              {isOrderClosing ? (
                <Badge
                  variant="outline"
                  className={
                    collapsed
                      ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                      : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"
                  }
                >
                  {t("story.chapters.closing")}
                </Badge>
              ) : null}
            </div>

            <h2
              className={
                collapsed
                  ? "truncate border-b border-ink/10 pb-1 font-display text-[clamp(1.55rem,2.2vw,2rem)] leading-none text-foreground"
                  : "border-b border-ink/10 pb-3 font-display text-[clamp(2.15rem,4vw,3.5rem)] leading-[0.95] text-foreground"
              }
            >
              {documentTitle || t("story.chapters.untitled", { defaultValue: "Untitled chapter" })}
            </h2>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-3">
            <div className="flex items-start gap-3">
              <Button
                aria-label={toggleLabel}
                className="h-8 w-8 rounded-full border border-[#dcccb7] bg-white p-0 text-muted-foreground shadow-none transition-all duration-300 ease-out hover:bg-[#f8f3eb] hover:text-foreground"
                onClick={() => setCollapsed((current) => !current)}
                type="button"
                variant="ghost"
              >
                <span className="transition-transform duration-300 ease-out">
                  {collapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </span>
              </Button>
            </div>

            {statusContent ? (
              <p
                aria-live="polite"
                className="inline-flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground transition-all duration-300 ease-out"
              >
                {statusContent}
              </p>
            ) : null}

            {collapsed && chapter.headerImageUrl ? (
              <button
                aria-label={t("story.attachments.chapterImageAlt", {
                  title: chapter.title,
                })}
                className="block w-full max-w-[8.5rem] overflow-hidden rounded-[16px] border border-border/60 bg-white shadow-sm transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setLightboxOpen(true)}
                type="button"
              >
                <img
                  alt={t("story.attachments.chapterImageAlt", {
                    title: chapter.title,
                  })}
                  className="h-24 w-full object-cover transition-all duration-300 ease-out hover:scale-[1.02]"
                  src={chapter.headerImageUrl}
                />
              </button>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {saveError}
          </p>
        ) : null}
      </header>

      <div
        className={
          collapsed
            ? "pointer-events-none max-h-0 overflow-hidden border-t border-ink/10 pt-0 opacity-0 -translate-y-2 transition-all duration-300 ease-out"
            : "max-h-[28rem] overflow-hidden border-t border-ink/10 pt-6 opacity-100 translate-y-0 transition-all duration-300 ease-out"
        }
      >
        <input
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          data-testid="chapter-header-image-input"
          onChange={(event) => {
            void handleUploadHeaderImage(event.target.files?.[0] ?? null);
          }}
          type="file"
        />

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_12rem]">
              <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[13rem_minmax(0,1fr)] md:items-end">
              <div className="space-y-2">
                <Label htmlFor="chapter-status">
                  {t("story.chapters.fields.status")}
                </Label>
                <Select
                  onValueChange={(value) =>
                    setStatus(value as ChapterView["status"])
                  }
                  value={status}
                >
                  <SelectTrigger
                    aria-label={t("story.chapters.fields.status")}
                    id="chapter-status"
                  >
                    <SelectValue
                      placeholder={t("story.chapters.fields.statusPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {chapterStatuses.map((nextStatus) => (
                      <SelectItem key={nextStatus} value={nextStatus}>
                        {t(`story.statuses.${nextStatus}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-end justify-start gap-2 md:justify-end">
                {headerActions ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {headerActions}
                  </div>
                ) : null}
                <Button
                  disabled={!onGenerateHeader}
                  onClick={() => void onGenerateHeader?.()}
                  type="button"
                  variant="secondary"
                >
                  <Sparkles className="h-4 w-4" />
                  {t("story.attachments.generateIllustration")}
                </Button>
                {onUploadHeaderImage ? (
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    variant="outline"
                  >
                    {chapter.hasHeaderImage ? (
                      <Upload className="h-4 w-4" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    {chapter.hasHeaderImage
                      ? t("story.attachments.replaceImage")
                      : t("story.attachments.uploadImage")}
                  </Button>
                ) : null}
                {chapter.hasHeaderImage && onRemoveHeaderImage ? (
                  <Button
                    onClick={() => void handleRemoveHeaderImage()}
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("story.attachments.removeImage")}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">
                {t("story.attachments.title")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("story.attachments.chapterDescription")}
              </p>
              <p className="text-xs leading-5 text-muted-foreground">
                {getRoleHint()}
              </p>
            </div>
          </div>

          <div className="flex xl:justify-end">
            {chapter.headerImageUrl ? (
              <button
                className="block w-full max-w-[12rem]"
                onClick={() => setLightboxOpen(true)}
                type="button"
              >
                <img
                  alt={t("story.attachments.chapterImageAlt", {
                    title: chapter.title,
                  })}
                  className="h-44 w-full rounded-[24px] object-cover transition-transform duration-200 hover:scale-[1.02]"
                  src={chapter.headerImageUrl}
                />
              </button>
            ) : (
              <div className="flex h-44 w-full max-w-[12rem] items-center justify-center rounded-[24px] border border-dashed border-border bg-secondary/20 px-4 text-center text-sm text-muted-foreground">
                {t("story.attachments.emptyChapterImage")}
              </div>
            )}
          </div>
        </div>
      </div>

      <LoreEditor
        autofocus
        content={body}
        contentClassName="lore-editor-page-breaks"
        enhanceSelection={
          textEnhancement
            ? {
                getCreditCost: textEnhancement.getEnhanceCreditCost,
                getDisabledReason: textEnhancement.getEnhanceDisabledReason,
                loading: textEnhancement.enhanceState.status === "loading",
                onRequest: textEnhancement.onRequestSelectionEnhance,
                onSelectionChange: textEnhancement.onSelectionChanged,
              }
            : undefined
        }
        onEditorApiChange={(api) => {
          editorApiRef.current = api;
        }}
        onChange={setBody}
        paginated={false}
        placeholder={t("story.chapters.editorPlaceholder")}
      />

      {textEnhancement ? (
        <div className="space-y-4">
          <EnhanceSuggestionPanel
            onDismiss={textEnhancement.onDismissEnhance}
            onPromptAction={() => textEnhancement.onPromptAction(textEnhancement.enhanceState.prompt)}
            onReplace={handleReplaceEnhanceSuggestion}
            state={textEnhancement.enhanceState}
          />
          {textEnhancement.polishState.status !== "idle" ? (
            <PolishDiffPanel
              onApply={handleApplyPolishSuggestion}
              onDismiss={textEnhancement.onDismissPolish}
              onPromptAction={() => textEnhancement.onPromptAction(textEnhancement.polishState.prompt)}
              state={textEnhancement.polishState}
            />
          ) : null}
        </div>
      ) : null}

      {afterEditor}

      <ImageLightbox
        alt={t("story.attachments.chapterImageAlt", { title: chapter.title })}
        emptyMessage={t("story.messages.mediaRecoveryBody")}
        imageUrl={chapter.headerImageUrl}
        onOpenChange={setLightboxOpen}
        open={lightboxOpen}
        title={t("story.attachments.chapterImageLightboxTitle", {
          title: chapter.title,
        })}
      />
    </section>
  );
}
